import { morphSection } from '@theme/section-renderer';
import { CartUpdateEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';

// Checkbox toggle: subscribe to default or unsubscribe
document.addEventListener('change', async (event) => {
  const checkbox = event.target;
  if (!(checkbox instanceof HTMLInputElement) || !checkbox.classList.contains('cart-sub-upsell__checkbox')) return;

  const upsell = checkbox.closest('.cart-sub-upsell');
  if (!upsell) return;

  const itemKey = checkbox.dataset.itemKey;
  const defaultPlanId = checkbox.dataset.defaultPlanId;
  const sellingPlanId = checkbox.checked ? defaultPlanId : '';
  
  if (!itemKey) return;

  const cartItemsComponent = checkbox.closest('cart-items-component');
  const sectionId = cartItemsComponent?.dataset.sectionId;
  if (!sectionId) return;

  // Disable during update
  checkbox.disabled = true;
  upsell.querySelectorAll('button').forEach((b) => { b.disabled = true; });
  cartItemsComponent?.classList.add('cart-items-disabled');

  const body = JSON.stringify({
    id: itemKey,
    selling_plan: sellingPlanId,
    sections: sectionId,
    sections_url: window.location.pathname,
  });

  try {
    const response = await fetch(Theme.routes.cart_change_url, fetchConfig('json', { body }));
    const parsedResponse = await response.json();

    if (parsedResponse.errors) {
      console.error('Subscription update error:', parsedResponse.errors);
      checkbox.checked = !checkbox.checked;
      return;
    }

    const sectionHtml = parsedResponse.sections?.[sectionId];
    if (sectionHtml) {
      const isDrawer = Boolean(cartItemsComponent?.hasAttribute('data-drawer'));
      morphSection(sectionId, sectionHtml, { mode: isDrawer ? 'hydration' : 'full' });
    }

    cartItemsComponent?.dispatchEvent(
      new CartUpdateEvent(parsedResponse, sectionId, {
        itemCount: parsedResponse.item_count,
        source: 'cart-subscription-upsell',
        sections: parsedResponse.sections,
      })
    );
  } catch (error) {
    console.error('Subscription update failed:', error);
    checkbox.checked = !checkbox.checked;
  } finally {
    checkbox.disabled = false;
    upsell.querySelectorAll('button').forEach((b) => { b.disabled = false; });
    cartItemsComponent?.classList.remove('cart-items-disabled');
  }
});

// Dropdown summary toggle
document.addEventListener('click', (event) => {
  const summary = event.target.closest('.spp__dropdown-summary');
  if (!summary) return;

  // Scope to the cart upsell, like the plan-item handler below. This markup is shared
  // with blocks/selling-plan-picker.liquid, whose own toggle runs first (the theme
  // delegates `on:click` from document during the capture phase) — an unscoped handler
  // here would immediately undo it and the PDP dropdown would never open.
  if (!summary.closest('.cart-sub-upsell')) return;

  const frequency = summary.closest('.spp__frequency');
  if (!frequency) return;

  const isExpanded = summary.getAttribute('aria-expanded') === 'true';
  summary.setAttribute('aria-expanded', String(!isExpanded));
  
  if (isExpanded) {
    frequency.classList.remove('spp__frequency--expanded');
  } else {
    frequency.classList.add('spp__frequency--expanded');
  }
});

// Plan button click: mark selected and update cart
document.addEventListener('click', async (event) => {
  const btn = event.target.closest('.spp__plan-item');
  if (!btn || btn.disabled) return;

  const upsell = btn.closest('.cart-sub-upsell');
  if (!upsell) return;
  const checkbox = upsell.querySelector('.cart-sub-upsell__checkbox');

  const itemKey = btn.dataset.itemKey;
  const sellingPlanId = btn.dataset.planId;
  if (!itemKey || !sellingPlanId) return;

  const cartItemsComponent = btn.closest('cart-items-component');
  const sectionId = cartItemsComponent?.dataset.sectionId;
  if (!sectionId) return;

  // Disable during update
  if(checkbox) checkbox.disabled = true;
  upsell.querySelectorAll('.spp__plan-item').forEach((b) => {
    // There is no disabled property on a div, but we can prevent clicks via CSS or custom property
    b.setAttribute('data-disabled', 'true');
    b.style.pointerEvents = 'none';
  });
  cartItemsComponent?.classList.add('cart-items-disabled');

  const body = JSON.stringify({
    id: itemKey,
    selling_plan: sellingPlanId,
    sections: sectionId,
    sections_url: window.location.pathname,
  });

  try {
    const response = await fetch(Theme.routes.cart_change_url, fetchConfig('json', { body }));
    const parsedResponse = await response.json();

    if (parsedResponse.errors) {
      console.error('Subscription upgrade error:', parsedResponse.errors);
      return;
    }

    const sectionHtml = parsedResponse.sections?.[sectionId];
    if (sectionHtml) {
      const isDrawer = Boolean(cartItemsComponent?.hasAttribute('data-drawer'));
      morphSection(sectionId, sectionHtml, { mode: isDrawer ? 'hydration' : 'full' });
    }

    cartItemsComponent?.dispatchEvent(
      new CartUpdateEvent(parsedResponse, sectionId, {
        itemCount: parsedResponse.item_count,
        source: 'cart-subscription-upsell',
        sections: parsedResponse.sections,
      })
    );
  } catch (error) {
    console.error('Subscription upgrade failed:', error);
  } finally {
    if(checkbox) checkbox.disabled = false;
    upsell.querySelectorAll('.spp__plan-item').forEach((b) => {
      b.removeAttribute('data-disabled');
      b.style.pointerEvents = 'auto';
    });
    cartItemsComponent?.classList.remove('cart-items-disabled');
  }
});

// Tooltip positioning: the tooltip is `position: fixed` so it isn't clipped by the
// cart drawer's `overflow-y: auto` scroll container. Its screen position has to be
// computed from the icon's rect instead of relying on CSS alone.
//
// The drawer's own <dialog> keeps a `transform` applied (its open/close animation
// uses `animation-fill-mode: forwards`), which makes IT the containing block for
// `position: fixed` descendants instead of the viewport. Coordinates have to be
// re-based against whichever ancestor actually ends up playing that role.
let activeTooltipInfo = null;

function getFixedContainingBlock(el) {
  for (let node = el.parentElement; node && node !== document.body; node = node.parentElement) {
    const style = getComputedStyle(node);
    if (
      style.transform !== 'none' ||
      style.perspective !== 'none' ||
      style.filter !== 'none' ||
      style.backdropFilter !== 'none' ||
      /transform|perspective|filter/.test(style.willChange) ||
      /paint|layout|strict|content/.test(style.contain)
    ) {
      return node;
    }
  }
  return null;
}

const VIEWPORT_MARGIN = 8;

function readPx(styles, name, fallback) {
  const value = parseFloat(styles.getPropertyValue(name));
  return Number.isNaN(value) ? fallback : value;
}

function positionUpsellTooltip(info) {
  const tooltip = info.querySelector('.cart-sub-upsell__tooltip');
  if (!tooltip) return;

  const styles = getComputedStyle(tooltip);
  const arrowInsetBase = readPx(styles, '--tooltip-arrow-inset-base', 20);
  const arrowSize = readPx(styles, '--tooltip-arrow-size', 8);
  const gap = readPx(styles, '--tooltip-gap', 12);

  const iconRect = info.getBoundingClientRect();
  const { width, height } = tooltip.getBoundingClientRect();
  const iconCenterX = iconRect.left + iconRect.width / 2;

  // Line the arrow up under the icon, clamp the panel into the viewport, then hand the
  // arrow back whatever the clamp took so it keeps pointing at the icon.
  const idealLeft = iconCenterX - width + arrowInsetBase;
  const maxLeft = Math.max(VIEWPORT_MARGIN, window.innerWidth - width - VIEWPORT_MARGIN);
  let left = Math.min(Math.max(idealLeft, VIEWPORT_MARGIN), maxLeft);

  const arrowInset = Math.min(
    Math.max(left + width - iconCenterX, arrowSize + 2),
    width - arrowSize - 2
  );
  tooltip.style.setProperty('--tooltip-arrow-inset', `${arrowInset}px`);

  // Flip below the icon when there isn't room above it.
  const placeBelow = iconRect.top - height - gap < VIEWPORT_MARGIN;
  let top = placeBelow ? iconRect.bottom + gap : iconRect.top - height - gap;
  tooltip.dataset.placement = placeBelow ? 'bottom' : 'top';

  const containingBlock = getFixedContainingBlock(tooltip);
  if (containingBlock) {
    const containingBlockRect = containingBlock.getBoundingClientRect();
    top -= containingBlockRect.top;
    left -= containingBlockRect.left;
  }

  tooltip.style.top = `${top}px`;
  tooltip.style.left = `${left}px`;
}

function openUpsellTooltip(info) {
  info.dataset.tooltipOpen = 'true';
  delete info.dataset.tooltipSuppressed;
  activeTooltipInfo = info;
  positionUpsellTooltip(info);
}

function closeUpsellTooltip(info, { suppressHover = false } = {}) {
  delete info.dataset.tooltipOpen;
  if (suppressHover) info.dataset.tooltipSuppressed = 'true';
  else delete info.dataset.tooltipSuppressed;
  if (activeTooltipInfo === info) activeTooltipInfo = null;
}

document.addEventListener('click', (event) => {
  const info = event.target.closest('.cart-sub-upsell__info');

  document.querySelectorAll('.cart-sub-upsell__info[data-tooltip-open="true"]').forEach((other) => {
    if (other !== info) closeUpsellTooltip(other);
  });

  if (!info) return;

  // The icon and its tooltip both sit inside the <label> that toggles the subscription
  // checkbox, so a click here would otherwise subscribe/unsubscribe the line item.
  event.preventDefault();
  event.stopPropagation();

  if (info.dataset.tooltipOpen === 'true') {
    closeUpsellTooltip(info, { suppressHover: true });
    info.blur();
  } else {
    openUpsellTooltip(info);
  }
});

document.addEventListener('keydown', (event) => {
  if (event.key !== 'Escape') return;
  document.querySelectorAll('.cart-sub-upsell__info[data-tooltip-open="true"]').forEach((info) => {
    closeUpsellTooltip(info);
    info.blur();
  });
});

document.addEventListener('mouseover', (event) => {
  const info = event.target.closest('.cart-sub-upsell__info');
  if (!info) return;
  activeTooltipInfo = info;
  positionUpsellTooltip(info);
});

document.addEventListener('focusin', (event) => {
  const info = event.target.closest('.cart-sub-upsell__info');
  if (!info) return;
  activeTooltipInfo = info;
  positionUpsellTooltip(info);
});

document.addEventListener('mouseout', (event) => {
  const info = event.target.closest('.cart-sub-upsell__info');
  if (!info || info.contains(event.relatedTarget)) return;

  // The pointer has left the icon, so :hover can no longer fight a click-close.
  delete info.dataset.tooltipSuppressed;

  // A click-opened tooltip stays open (and stays tracked, so scrolling keeps it anchored).
  if (activeTooltipInfo === info && info.dataset.tooltipOpen !== 'true') {
    activeTooltipInfo = null;
  }
});

document.addEventListener('focusout', (event) => {
  const info = event.target.closest('.cart-sub-upsell__info');
  if (!info || info.dataset.tooltipOpen === 'true') return;
  if (activeTooltipInfo === info) activeTooltipInfo = null;
});

window.addEventListener(
  'scroll',
  () => {
    if (activeTooltipInfo) positionUpsellTooltip(activeTooltipInfo);
  },
  { capture: true, passive: true }
);

window.addEventListener('resize', () => {
  if (activeTooltipInfo) positionUpsellTooltip(activeTooltipInfo);
});

// The mobile menu drawer renders its own copy of the cart drawer, so Shopify's native subscription
// disclosure — and its `id="shopify-subscription-policy-button"` link — exists twice on the page.
// Shopify's portable-wallets script binds the click handler with `document.getElementById(...)`,
// which only ever finds the first copy (the hidden mobile one), leaving the link customers actually
// see inert. Forward the click to whichever copy Shopify bound.
document.addEventListener('click', (event) => {
  const clicked = event.target?.closest?.('#shopify-subscription-policy-button');
  if (!clicked) return;

  const bound = document.getElementById('shopify-subscription-policy-button');
  if (!bound || bound === clicked) return;

  bound.click();
});

// Shopify's native subscription disclosure renders its "Cancellation policy" link as
// <top-level-modal>, a custom element it appends directly to <body> on click and removes again
// on close. Its shadow DOM overlay is just `position: fixed; z-index: 2147483647`, which loses
// twice over to the cart drawer: the drawer's <dialog> is shown with `showModal()`, so it paints
// from the browser's top layer (above any z-index) *and* it makes everything outside itself inert,
// so the policy modal wouldn't even receive the click on its own close button.
//
// Both only go away if the policy modal becomes the topmost modal itself, so move it into a
// <dialog> of ours and `showModal()` that.
const subscriptionPolicyPortal = document.createElement('dialog');
subscriptionPolicyPortal.id = 'subscription-policy-portal';
subscriptionPolicyPortal.style.cssText =
  'border:none;padding:0;background:transparent;max-width:none;max-height:none;width:100%;height:100%;overflow:visible;';
document.body.appendChild(subscriptionPolicyPortal);

new MutationObserver((mutations) => {
  for (const mutation of mutations) {
    for (const node of mutation.addedNodes) {
      if (node.nodeName !== 'TOP-LEVEL-MODAL' || node.parentElement !== document.body) continue;

      // The move re-runs the widget's connectedCallback, which re-snapshots <body>'s inline styles
      // for its scroll lock. By then those are the *locked* values, so on close it would restore
      // `overflow: hidden` and leave the page unscrollable. Keep its original snapshot.
      const scrollLock = { ...node.originalStyle };
      const scrollPosition = node.scrollPosition;

      subscriptionPolicyPortal.appendChild(node);

      node.originalStyle = scrollLock;
      node.scrollPosition = scrollPosition;

      if (!subscriptionPolicyPortal.open) subscriptionPolicyPortal.showModal();
    }
  }
}).observe(document.body, { childList: true });

// The close button and a backdrop click both close the widget by removing its own element, so the
// portal is done once it's empty.
new MutationObserver(() => {
  if (subscriptionPolicyPortal.open && !subscriptionPolicyPortal.firstElementChild) {
    subscriptionPolicyPortal.close();
  }
}).observe(subscriptionPolicyPortal, { childList: true });

// Escape has to stop at the policy modal. Left alone it cascades: the widget's own document-level
// handler removes it, the portal empties and closes, and the browser's Escape handling then lands on
// the drawer — closing it natively, behind the theme's closeDialog(), which is what restores the
// scroll lock on <body>. Silencing the event leaves the portal as the topmost modal, so Escape
// closes that instead and the drawer is left alone.
document.addEventListener(
  'keydown',
  (event) => {
    if (event.key === 'Escape' && subscriptionPolicyPortal.open) event.stopPropagation();
  },
  { capture: true }
);

subscriptionPolicyPortal.addEventListener('close', () => {
  // Closing the portal leaves the widget mounted, so trigger its own teardown rather than dropping
  // it — that is what releases the scroll lock it applied to <body>.
  const widget = subscriptionPolicyPortal.firstElementChild;
  widget?.shadowRoot?.querySelector('#close-icon')?.click();
});
